import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errDetail, fmtDate, fmtEur } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Truck, CheckCircle2, XCircle, Plus, Trash2, ShieldCheck, ShieldAlert, FileText, Upload, Download, AlertTriangle, Users, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "../components/ui/checkbox";
import { useApp } from "../lib/store";
import { AccountActivities } from "../components/AccountRelated";
import { VisitNotes } from "../components/VisitNotes";
import { AccountNetwork } from "../components/AccountExtras";
import { AccountAnalysis } from "../components/AccountAnalysis";
import { WalletShare } from "../components/WalletShare";
import { SupplierContactsPanel } from "../components/SupplierContactsPanel";
import { ActivityForm } from "./Activities";
import { Award, Pencil } from "lucide-react";
import { SupplierEditDialog } from "../components/srm/SupplierEditDialog";
import { SupplierArticlesSpend } from "../components/srm/SupplierArticlesSpend";
import { SupplierAiEvaluation } from "../components/srm/SupplierAiEvaluation";
import { useCommodityGroups, supplierTypeLabel } from "../lib/commodity";

const SC_GRADE = { A: "bg-green-100 text-green-700", B: "bg-blue-100 text-blue-700", C: "bg-amber-100 text-amber-800", D: "bg-red-100 text-red-700" };

const ROLE_LABELS = { supplier: "Lieferant", prospect_supplier: "Potenzieller Lieferant", customer: "Kunde" };
const STATUS_COLORS = {
  prospect: "bg-gray-100 text-gray-700", registered: "bg-blue-100 text-blue-700",
  qualified: "bg-indigo-100 text-indigo-700", approved: "bg-green-100 text-green-700",
  restricted: "bg-amber-100 text-amber-800", blocked: "bg-red-100 text-red-700",
};
const STATUSES = ["prospect", "registered", "qualified", "approved", "restricted", "blocked"];
const ALL_ROLES = ["supplier", "prospect_supplier", "customer"];
const DOC_STATUS = {
  valid: ["bg-green-100 text-green-700", "gültig"],
  expiring_90: ["bg-blue-100 text-blue-700", "läuft ab ≤90 Tage"],
  expiring_30: ["bg-amber-100 text-amber-800", "läuft ab ≤30 Tage"],
  expired: ["bg-red-100 text-red-700", "abgelaufen"],
  missing: ["bg-gray-100 text-gray-500", "fehlt"],
};

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasIntel } = useApp();
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [banks, setBanks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [caps, setCaps] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [plants, setPlants] = useState([]);
  const { pathsFor } = useCommodityGroups();
  const canWrite = caps.includes("supplier_write");
  const canComm = caps.includes("see_supplier_commercial");
  const canVerify = caps.includes("bank_verify");

  const load = useCallback(() => {
    api.get(`/suppliers/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(errDetail(e)));
    api.get(`/suppliers/${id}/plant-matrix`).then((r) => setMatrix(r.data)).catch(() => {});
    api.get(`/suppliers/${id}/audit`).then((r) => setAudit(r.data || [])).catch(() => {});
  }, [id]);
  const loadBanks = useCallback(() => {
    api.get(`/suppliers/${id}/bank-accounts`).then((r) => setBanks(r.data || [])).catch(() => setBanks([]));
  }, [id]);
  useEffect(() => { api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/plants").then((r) => setPlants(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canComm || canVerify) loadBanks(); }, [canComm, canVerify, loadBanks]);

  if (!data) return <div className="p-6 text-gray-400">Lädt…</div>;
  const p = data.profile || {};

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="supplier-detail-page">
      <button onClick={() => navigate("/suppliers")} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#1B2A4A] mb-4"><ArrowLeft className="h-4 w-4" /> Lieferanten</button>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><Truck className="h-6 w-6 text-[#F5A623]" /></div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-[#1B2A4A]" data-testid="supplier-name">{data.name}</h1>
              <Badge className={STATUS_COLORS[p.status] || "bg-gray-100"} data-testid="supplier-status-badge">{p.status || "–"}</Badge>
              {(data.roles || []).map((r) => <Badge key={r} variant="outline" data-testid={`supplier-role-${r}`}>{ROLE_LABELS[r] || r}</Badge>)}
            </div>
            <p className="text-sm text-gray-500 font-mono">{data.account_number} · {[data.city, data.country].filter(Boolean).join(", ")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="supplier-edit-btn"><Pencil className="h-4 w-4 mr-1" />Bearbeiten</Button>}
          {canWrite && <RolesButton id={id} roles={data.roles || []} onChanged={load} />}
          {canWrite && <StatusButton id={id} status={p.status} onChanged={load} />}
        </div>
      </div>

      {editOpen && (
        <SupplierEditDialog data={data} plants={plants} canComm={canComm}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }} />
      )}

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-gray-200 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="supplier-tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="supplier-tab-vendors">Kreditoren & Werke</TabsTrigger>
          <TabsTrigger value="bank" data-testid="supplier-tab-bank">Bankverbindungen</TabsTrigger>
          <TabsTrigger value="documents" data-testid="supplier-tab-documents">Dokumente</TabsTrigger>
          <TabsTrigger value="orders" data-testid="supplier-tab-orders">Bestellungen</TabsTrigger>
          <TabsTrigger value="articles" data-testid="supplier-tab-articles">Artikel & Umsatz</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="supplier-tab-contacts">Kontakte</TabsTrigger>
          <TabsTrigger value="activities" data-testid="supplier-tab-activities">Aktivitäten</TabsTrigger>
          <TabsTrigger value="visit_notes" data-testid="supplier-tab-visit-notes">Besuche & Audits</TabsTrigger>
          <TabsTrigger value="scorecard" data-testid="supplier-tab-scorecard">Scorecard</TabsTrigger>
          <TabsTrigger value="network" data-testid="supplier-tab-network">Netzwerk</TabsTrigger>
          {hasIntel && <TabsTrigger value="analysis" data-testid="supplier-tab-analysis">KI-Analyse</TabsTrigger>}
          {hasIntel && <TabsTrigger value="wallet" data-testid="supplier-tab-wallet">Anteil & Volumen</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="supplier-tab-audit">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-white rounded-xl border border-gray-200 p-5" data-testid="supplier-overview">
            <Field label="Rechtsform" value={data.legal_form} />
            <Field label="USt-IdNr." value={data.vat_id} />
            <Field label="DUNS" value={data.duns_number} />
            <Field label="EORI" value={data.eori} />
            <Field label="Steuernummer" value={data.tax_number} />
            <Field label="Branche" value={data.industry} />
            <Field label="Gründungsjahr" value={p.founded_year != null ? String(p.founded_year) : ""} />
            <Field label="Tier" value={p.tier} />
            <div className="col-span-2 md:col-span-3">
              <div className="text-xs text-gray-400 mb-1">Lieferantenart</div>
              <div className="flex flex-wrap gap-1.5">
                {(p.supplier_types || []).length ? (p.supplier_types || []).map((tp) => (
                  <Badge key={tp} className="bg-[#1B2A4A] text-white" data-testid={`supplier-type-badge-${tp}`}>{supplierTypeLabel(tp)}</Badge>
                )) : <span className="text-sm text-gray-400">–</span>}
              </div>
            </div>
            <div className="col-span-2 md:col-span-3">
              <div className="text-xs text-gray-400 mb-1">Warengruppen</div>
              <div className="flex flex-wrap gap-1.5" data-testid="supplier-commodity-groups">
                {(() => { const paths = pathsFor(p.commodity_group_ids); const list = paths.length ? paths : (p.categories || []); return list.length ? list.map((c) => <Badge key={c} variant="outline">{c}</Badge>) : <span className="text-sm text-gray-400">–</span>; })()}
              </div>
            </div>
            <div className="col-span-2 md:col-span-3">
              <div className="text-xs text-gray-400 mb-1">Belieferte Standorte</div>
              <div className="flex flex-wrap gap-1.5" data-testid="supplier-supplied-plants">
                {(p.supplied_plant_ids || []).length ? (p.supplied_plant_ids || []).map((pid) => {
                  const pl = plants.find((x) => x.id === pid); return <Badge key={pid} className="bg-[#F5A623]/20 text-[#1B2A4A] border border-[#F5A623]">{pl ? pl.name : pid}</Badge>;
                }) : <span className="text-sm text-gray-400">–</span>}
              </div>
            </div>
            <div className="col-span-2 md:col-span-3">
              <div className="text-xs text-gray-400 mb-1">Zertifizierungen</div>
              <div className="flex flex-wrap gap-1.5" data-testid="supplier-certs">
                {(p.certifications || []).length ? (p.certifications || []).map((c) => <Badge key={c} className="bg-green-100 text-green-700">{c}</Badge>) : <span className="text-sm text-gray-400">–</span>}
              </div>
            </div>
            <Field label="Lieferzeit" value={p.lead_time_days != null ? `${p.lead_time_days} Tage` : ""} />
            <Field label="Website" value={data.website} />
            <Field label="Telefon" value={data.phone} />
            <Field label="E-Mail" value={data.email} />
            <Field label="Status-Grund" value={p.status_reason} />
            <Field label="Sanktionsprüfung" value={p.sanctions_result} />
            {canComm ? <>
              <Field label="Zahlungsziel" value={p.payment_terms_text || (p.payment_terms_days ? `${p.payment_terms_days} Tage` : "")} />
              <Field label="Incoterm" value={p.default_incoterm} />
              <Field label="Währung" value={p.default_currency} />
              <Field label="Mindestbestellwert" value={p.min_order_value != null ? `${p.min_order_value} ${p.min_order_currency || ""}` : ""} />
              <Field label="Risiko-Score" value={p.risk_score != null ? String(p.risk_score) : ""} />
            </> : <div className="col-span-3 text-sm text-gray-400 italic">Kaufmännische Daten für Ihre Rolle ausgeblendet</div>}
          </div>
        </TabsContent>

        <TabsContent value="articles" className="mt-4">
          <SupplierArticlesSpend supplierId={id} />
        </TabsContent>

        <TabsContent value="vendors" className="mt-4 space-y-6">
          <VendorNumbers id={id} vendorNumbers={data.vendor_numbers || []} canWrite={canWrite} onChanged={load} />
          <PlantMatrix matrix={matrix} />
        </TabsContent>

        <TabsContent value="bank" className="mt-4">
          {canComm || canVerify
            ? <BankAccounts id={id} banks={banks} canVerify={canVerify} canWrite={canComm} onChanged={loadBanks} />
            : <Empty text="Bankverbindungen sind für Ihre Rolle nicht sichtbar." />}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <DocumentsPanel id={id} caps={caps} />
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <SupplierOrders id={id} />
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <SupplierContactsPanel supplierId={id} supplierName={data.name} canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="activities" className="mt-4">
          <SupplierActivities id={id} supplierName={data.name} canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="visit_notes" className="mt-4">
          <VisitNotes accountId={id} />
        </TabsContent>

        <TabsContent value="scorecard" className="mt-4 space-y-6">
          <SupplierAiEvaluation id={id} />
          <SupplierScorecard id={id} />
        </TabsContent>

        <TabsContent value="network" className="mt-4">
          <AccountNetwork accountId={id} />
        </TabsContent>

        {hasIntel && (
          <TabsContent value="analysis" className="mt-4">
            <AccountAnalysis accountId={id} />
          </TabsContent>
        )}

        {hasIntel && (
          <TabsContent value="wallet" className="mt-4">
            <WalletShare accountId={id} />
          </TabsContent>
        )}

        <TabsContent value="audit" className="mt-4">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="supplier-audit-list">
            <Table>
              <TableHeader><TableRow><TableHead>Zeit</TableHead><TableHead>Aktion</TableHead><TableHead>Benutzer</TableHead><TableHead>Notiz</TableHead></TableRow></TableHeader>
              <TableBody>
                {audit.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-6">Keine Ereignisse</TableCell></TableRow>}
                {audit.map((e) => (
                  <TableRow key={e.id}><TableCell className="text-xs text-gray-500">{fmtDate(e.at)}</TableCell>
                    <TableCell className="font-medium">{e.action}</TableCell><TableCell className="text-sm">{e.actor_name}</TableCell>
                    <TableCell className="text-sm text-gray-500">{e.note}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SupplierActivities({ id, supplierName, canWrite }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(0);
  return (
    <div className="space-y-3" data-testid="supplier-activities-wrap">
      {canWrite && (
        <div className="flex justify-end">
          <Button size="sm" data-testid="supplier-activity-add-btn" className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Aktivität erfassen
          </Button>
        </div>
      )}
      <AccountActivities key={token} accountId={id} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-activity-dialog">
          <DialogHeader><DialogTitle>Aktivität erfassen</DialogTitle>
            <DialogDescription>Anruf, Meeting, Audit-Besuch oder Aufgabe zu diesem Lieferanten.</DialogDescription></DialogHeader>
          <ActivityForm
            initial={{ account_id: id, account_name: supplierName }}
            accounts={[{ id, name: supplierName }]}
            opps={[]}
            createEndpoint={`/suppliers/${id}/activities`}
            onSaved={() => { setOpen(false); setToken((tk) => tk + 1); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SupplierScorecard({ id }) {
  const [cards, setCards] = useState(null);
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get(`/srm/scorecards?supplier_id=${id}`).then((r) => setCards(r.data || [])).catch(() => setCards([]));
    api.get(`/srm/scorecard/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, [id]);
  if (!cards) return <div className="text-gray-400">Lädt…</div>;
  if (cards.length === 0) return <Empty text="Noch keine Scorecard für diesen Lieferanten — im Bereich „Scorecards“ berechnen." />;
  const sorted = [...cards].sort((a, b) => (b.period || "").localeCompare(a.period || ""));
  const latest = sorted[0];
  const labels = Object.fromEntries((cfg?.metrics || []).map((m) => [m.key, m.label]));
  return (
    <div className="space-y-4" data-testid="supplier-scorecard-panel">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2"><Award className="h-5 w-5 text-[#1B2A4A]" />
            <h3 className="font-semibold text-[#1B2A4A]">Aktuelle Scorecard · {latest.period}</h3></div>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-[#1B2A4A]" data-testid="supplier-sc-score">{latest.weighted_score}</span>
            <Badge className={SC_GRADE[latest.grade]} data-testid="supplier-sc-grade">{latest.grade}</Badge>
            <span className="text-sm text-gray-500">Peer-Perzentil {latest.peer_percentile}%</span>
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(latest.scores || {}).map(([k, v]) => (
            <div key={k} data-testid={`supplier-sc-metric-${k}`}>
              <div className="flex justify-between text-sm mb-1">
                <span>{labels[k] || k} {cfg?.weights?.[k] != null && <span className="text-gray-400">({cfg.weights[k]}%)</span>}</span>
                <span className="font-medium">{v}</span></div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[#1B2A4A]" style={{ width: `${v}%` }} /></div>
            </div>
          ))}
        </div>
        {latest.manual_adjustment && <div className="mt-3 rounded-lg bg-purple-50 border border-purple-200 p-2 text-sm text-purple-800">Manuell {latest.manual_adjustment.delta > 0 ? "+" : ""}{latest.manual_adjustment.delta} von {latest.manual_adjustment.by}: {latest.manual_adjustment.comment}</div>}
      </div>
      {sorted.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h3 className="font-semibold text-[#1B2A4A]">Verlauf</h3></div>
          <Table>
            <TableHeader><TableRow><TableHead>Periode</TableHead><TableHead className="text-right">Score</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Peer-Perzentil</TableHead></TableRow></TableHeader>
            <TableBody>
              {sorted.map((c) => (
                <TableRow key={c.id} data-testid={`supplier-sc-row-${c.period}`}>
                  <TableCell>{c.period}</TableCell>
                  <TableCell className="text-right font-medium">{c.weighted_score}</TableCell>
                  <TableCell><Badge className={SC_GRADE[c.grade]}>{c.grade}</Badge></TableCell>
                  <TableCell className="text-right text-gray-600">{c.peer_percentile}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div><div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div><div className="text-sm text-[#1B2A4A] mt-0.5">{value || "–"}</div></div>
);
const Empty = ({ text }) => <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-400">{text}</div>;

function SupplierOrders({ id }) {
  const [pos, setPos] = useState(null);
  useEffect(() => { api.get(`/srm/purchase-orders?supplier_id=${id}`).then((r) => setPos(r.data || [])).catch(() => setPos([])); }, [id]);
  if (!pos) return <div className="text-gray-400">Lädt…</div>;
  if (pos.length === 0) return <Empty text="Noch keine Bestellungen für diesen Lieferanten." />;
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="supplier-orders-list">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100"><ShoppingCart className="h-4 w-4 text-[#1B2A4A]" /><h3 className="font-semibold text-[#1B2A4A]">Bestellungen</h3></div>
      <Table>
        <TableHeader><TableRow><TableHead>Nummer</TableHead><TableHead>Werk</TableHead><TableHead>Wert</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {pos.map((p) => (
            <TableRow key={p.id} className="hover:bg-gray-50" data-testid={`supplier-po-${p.po_number}`}>
              <TableCell><Link to="/purchase-orders" className="font-mono text-[#1B2A4A] hover:underline">{p.po_number}</Link></TableCell>
              <TableCell>{p.plant_name || "–"}</TableCell><TableCell>{fmtEur(p.total_value, p.currency)}</TableCell>
              <TableCell><Badge className="bg-gray-100 text-gray-700">{p.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatusButton({ id, status, onChanged }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(status);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setVal(status); setReason(""); } }, [open, status]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/suppliers/${id}/status`, { status: val, reason }); toast.success("Status geändert"); setOpen(false); onChanged(); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" data-testid="supplier-change-status-btn" onClick={() => setOpen(true)}>Status ändern</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-status-dialog">
          <DialogHeader><DialogTitle>Lieferantenstatus ändern</DialogTitle><DialogDescription>Bei Herabstufung/Sperrung ist ein Grund erforderlich.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Select value={val} onValueChange={setVal}><SelectTrigger data-testid="supplier-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            <div><Label>Grund</Label><Textarea data-testid="supplier-status-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="supplier-status-save-btn" disabled={busy} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RolesButton({ id, roles, onChanged }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(roles);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setSel(roles); }, [open, roles]);
  const toggle = (r) => setSel((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/accounts/${id}/roles`, { roles: sel }); toast.success("Rollen aktualisiert"); setOpen(false); onChanged(); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" data-testid="supplier-roles-btn" onClick={() => setOpen(true)}><Users className="h-4 w-4 mr-1" /> Rollen</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-roles-dialog">
          <DialogHeader><DialogTitle>Rollen dieser Firma</DialogTitle><DialogDescription>Eine Firma kann gleichzeitig Kunde und Lieferant sein.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {ALL_ROLES.map((r) => (
              <label key={r} className="flex items-center gap-3 cursor-pointer" data-testid={`supplier-role-toggle-${r}`}>
                <Checkbox checked={sel.includes(r)} onCheckedChange={() => toggle(r)} />
                <span className="text-sm text-[#1B2A4A]">{ROLE_LABELS[r]}</span>
              </label>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="supplier-roles-save-btn" disabled={busy || sel.length === 0} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DocumentsPanel({ id, caps }) {
  const canManage = caps.includes("doc_manage");
  const canOverride = caps.includes("compliance_override");
  const [data, setData] = useState(null);
  const [gate, setGate] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [ovOpen, setOvOpen] = useState(false);
  const [ovReason, setOvReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ document_type: "", title: "", issuing_body: "", register_reference: "", valid_from: "", valid_until: "" });
  const [file, setFile] = useState(null);

  const load = useCallback(() => {
    api.get(`/suppliers/${id}/documents`).then((r) => setData(r.data)).catch((e) => toast.error(errDetail(e)));
    api.get(`/suppliers/${id}/document-gate`).then((r) => setGate(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (addOpen) api.get("/srm/document-types").then((r) => setTypes(r.data || [])).catch(() => {}); }, [addOpen]);

  const upload = async () => {
    if (!form.document_type) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("file", file);
      await api.post(`/suppliers/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokument gespeichert"); setAddOpen(false);
      setForm({ document_type: "", title: "", issuing_body: "", register_reference: "", valid_from: "", valid_until: "" }); setFile(null);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const del = async (docId) => { try { await api.delete(`/supplier-documents/${docId}`); load(); } catch (e) { toast.error(errDetail(e)); } };
  const override = async () => {
    setBusy(true);
    try { await api.post(`/suppliers/${id}/document-gate/override`, { reason: ovReason }); toast.success("Override protokolliert"); setOvOpen(false); setOvReason(""); load(); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const download = async (fileId) => {
    try {
      const r = await api.get(`/files/download/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.headers["content-disposition"]?.split("filename=")[1] || "dokument";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!data) return <div className="text-gray-400">Lädt…</div>;
  const s = data.summary || {};
  const tiles = [["expired", "Abgelaufen", "text-red-600"], ["missing", "Fehlt (Pflicht)", "text-gray-500"], ["expiring_30", "≤30 Tage", "text-amber-600"], ["expiring_90", "≤90 Tage", "text-blue-600"]];

  return (
    <div className="space-y-4" data-testid="supplier-documents-panel">
      {gate && gate.blocked && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4" data-testid="document-gate-banner">
          <div className="flex items-start gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">Beschaffung gesperrt (Hard-Gate)</div>
              <div className="text-sm mt-0.5">{gate.reason}</div>
            </div>
            {canOverride && <Button size="sm" variant="outline" className="border-red-400 text-red-700" data-testid="document-gate-override-btn" onClick={() => setOvOpen(true)}>Override</Button>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map(([k, label, color]) => (
          <div key={k} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`doc-tile-${k}`}>
            <div className={`text-2xl font-bold ${color}`}>{s[k] || 0}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#1B2A4A]" /><h3 className="font-semibold text-[#1B2A4A]">Dokumenten- & Zertifikatsmatrix</h3></div>
          {canManage && <Button size="sm" data-testid="doc-add-btn" onClick={() => setAddOpen(true)}><Upload className="h-4 w-4 mr-1" /> Dokument hinzufügen</Button>}
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Dokument</TableHead><TableHead>Pflicht</TableHead><TableHead>Status</TableHead><TableHead>Gültig bis</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {(data.requirements || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-6">Keine Anforderungen</TableCell></TableRow>}
            {(data.requirements || []).map((r) => {
              const [cls, label] = DOC_STATUS[r.status] || ["bg-gray-100", r.status];
              return (
                <TableRow key={r.document_type} data-testid={`doc-row-${r.document_type}`}>
                  <TableCell className="font-medium text-[#1B2A4A]">{r.label}</TableCell>
                  <TableCell>{r.hard_gate ? <Badge className="bg-[#1B2A4A] text-white">Pflicht</Badge> : r.mandatory ? <Badge className="bg-indigo-100 text-indigo-700">erforderlich</Badge> : <span className="text-gray-400 text-xs">optional</span>}</TableCell>
                  <TableCell><Badge className={cls} data-testid={`doc-status-${r.document_type}`}>{label}{r.days_to_expiry != null && (r.status === "expiring_30" || r.status === "expiring_90") ? ` (${r.days_to_expiry}d)` : ""}</Badge></TableCell>
                  <TableCell className="text-sm text-gray-600">{r.valid_until ? fmtDate(r.valid_until) : (r.document ? "unbefristet" : "–")}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 justify-end">
                      {r.document?.file_id && <button title="Herunterladen" onClick={() => download(r.document.file_id)} className="text-gray-400 hover:text-[#1B2A4A]" data-testid={`doc-download-${r.document_type}`}><Download className="h-4 w-4" /></button>}
                      {canManage && r.document && <button title="Löschen" onClick={() => del(r.document.id)} className="text-gray-400 hover:text-red-600" data-testid={`doc-delete-${r.document_type}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="doc-add-dialog">
          <DialogHeader><DialogTitle>Dokument hinzufügen</DialogTitle><DialogDescription>PDF/DOCX optional; Metadaten und Gültigkeit steuern die Ablaufüberwachung.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Dokumenttyp *</Label>
              <Select value={form.document_type} onValueChange={(v) => setForm((f) => ({ ...f, document_type: v }))}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label_de || t.label_en}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Gültig ab</Label><Input type="date" data-testid="doc-valid-from" value={form.valid_from} onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))} /></div>
              <div><Label>Gültig bis</Label><Input type="date" data-testid="doc-valid-until" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} /></div>
            </div>
            <div><Label>Ausstellende Stelle</Label><Input value={form.issuing_body} onChange={(e) => setForm((f) => ({ ...f, issuing_body: e.target.value }))} /></div>
            <div><Label>Datei (PDF/DOCX, optional)</Label><Input type="file" accept=".pdf,.docx" data-testid="doc-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Abbrechen</Button>
            <Button data-testid="doc-save-btn" disabled={busy || !form.document_type} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={upload}>Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ovOpen} onOpenChange={setOvOpen}>
        <DialogContent data-testid="doc-override-dialog">
          <DialogHeader><DialogTitle>Hard-Gate übersteuern</DialogTitle><DialogDescription>Nur mit Compliance-Berechtigung. Wird protokolliert und im Monatsreport ausgewiesen.</DialogDescription></DialogHeader>
          <div><Label>Begründung *</Label><Textarea data-testid="doc-override-reason" value={ovReason} onChange={(e) => setOvReason(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setOvOpen(false)}>Abbrechen</Button>
            <Button data-testid="doc-override-save-btn" disabled={busy || !ovReason} className="bg-red-600 hover:bg-red-700" onClick={override}>Override protokollieren</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function VendorNumbers({ id, vendorNumbers, canWrite, onChanged }) {
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ erp_company_code: "", vendor_number: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/srm/erp-company-codes").then((r) => setCodes(r.data || [])).catch(() => {}); }, [open]);
  const add = async () => {
    setBusy(true);
    try { await api.post(`/suppliers/${id}/vendor-numbers`, form); toast.success("Lieferantennummer angelegt"); setOpen(false); setForm({ erp_company_code: "", vendor_number: "" }); onChanged(); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const del = async (vnId) => { try { await api.delete(`/vendor-numbers/${vnId}`); onChanged(); } catch (e) { toast.error(errDetail(e)); } };
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-[#1B2A4A]">Kreditorennummern je Buchungskreis</h3>
        {canWrite && <Button size="sm" data-testid="vendor-number-add-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Hinzufügen</Button>}
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Buchungskreis</TableHead><TableHead>Lieferantennummer</TableHead><TableHead>Aktiv</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {vendorNumbers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-6">Keine Kreditorennummern</TableCell></TableRow>}
          {vendorNumbers.map((v) => (
            <TableRow key={v.id} data-testid={`vendor-number-${v.id}`}>
              <TableCell className="font-mono">{v.erp_company_code}</TableCell><TableCell className="font-mono">{v.vendor_number}</TableCell>
              <TableCell>{v.is_active ? <Badge className="bg-green-100 text-green-700">aktiv</Badge> : <Badge className="bg-gray-100">inaktiv</Badge>}</TableCell>
              <TableCell>{canWrite && <button onClick={() => del(v.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vendor-number-dialog">
          <DialogHeader><DialogTitle>Kreditorennummer hinzufügen</DialogTitle><DialogDescription>Pro Buchungskreis/juristische Einheit eine Nummer.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Buchungskreis</Label>
              <Select value={form.erp_company_code} onValueChange={(v) => setForm((f) => ({ ...f, erp_company_code: v }))}>
                <SelectTrigger data-testid="vendor-code-select"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>{codes.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Lieferantennummer</Label><Input data-testid="vendor-number-input" value={form.vendor_number} onChange={(e) => setForm((f) => ({ ...f, vendor_number: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="vendor-number-save-btn" disabled={busy || !form.erp_company_code || !form.vendor_number} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={add}>Anlegen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlantMatrix({ matrix }) {
  if (!matrix) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="plant-matrix">
      <div className="px-4 py-3 border-b border-gray-100"><h3 className="font-semibold text-[#1B2A4A]">Transaktionsfähigkeit je Werk</h3></div>
      <Table>
        <TableHeader><TableRow><TableHead>Werk</TableHead><TableHead>Buchungskreis</TableHead><TableHead>Transaktionsfähig</TableHead></TableRow></TableHeader>
        <TableBody>
          {matrix.plants.map((p) => (
            <TableRow key={p.plant_id}><TableCell>{p.plant_name}</TableCell><TableCell className="font-mono">{p.erp_company_code}</TableCell>
              <TableCell>{p.transactable
                ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-4 w-4" /> ja</span>
                : <span className="inline-flex items-center gap-1 text-gray-400"><XCircle className="h-4 w-4" /> keine Kreditorennummer</span>}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BankAccounts({ id, banks, canVerify, canWrite, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const [verifyFor, setVerifyFor] = useState(null);
  const [form, setForm] = useState({ iban: "", bic: "", bank_name: "", account_holder: "" });
  const [vform, setVform] = useState({ verification_method: "", note: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try { await api.post(`/suppliers/${id}/bank-accounts`, form); toast.success("Bankverbindung angefragt (ausstehend)"); setAddOpen(false); setForm({ iban: "", bic: "", bank_name: "", account_holder: "" }); onChanged(); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post(`/bank-accounts/${verifyFor.id}/verify`, vform); toast.success("Bankverbindung verifiziert"); setVerifyFor(null); setVform({ verification_method: "", note: "" }); onChanged(); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const reject = async (b) => { try { await api.post(`/bank-accounts/${b.id}/reject`, { note: "" }); onChanged(); } catch (e) { toast.error(errDetail(e)); } };
  const bColor = { pending: "bg-amber-100 text-amber-800", verified: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700", inactive: "bg-gray-100" };
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1B2A4A]" /><h3 className="font-semibold text-[#1B2A4A]">Bankverbindungen (Vier-Augen-Prinzip)</h3></div>
        {canWrite && <Button size="sm" data-testid="bank-add-btn" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1" /> Änderung anfragen</Button>}
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>IBAN</TableHead><TableHead>Bank</TableHead><TableHead>Status</TableHead><TableHead>Anforderer</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {banks.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-6">Keine Bankverbindungen</TableCell></TableRow>}
          {banks.map((b) => (
            <TableRow key={b.id} data-testid={`bank-row-${b.id}`}>
              <TableCell className="font-mono">{b.iban_masked}</TableCell><TableCell>{b.bank_name}</TableCell>
              <TableCell><Badge className={bColor[b.status] || "bg-gray-100"} data-testid={`bank-status-${b.id}`}>{b.status}</Badge></TableCell>
              <TableCell className="text-sm">{b.requested_by_name}</TableCell>
              <TableCell>{b.status === "pending" && canVerify && (
                <div className="flex gap-2">
                  <button data-testid={`bank-verify-${b.id}`} onClick={() => setVerifyFor(b)} className="text-green-600 hover:text-green-800 inline-flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Verifizieren</button>
                  <button data-testid={`bank-reject-${b.id}`} onClick={() => reject(b)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1"><ShieldAlert className="h-4 w-4" /> Ablehnen</button>
                </div>)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="bank-add-dialog">
          <DialogHeader><DialogTitle>Bankverbindung anfragen</DialogTitle><DialogDescription>Wird als „ausstehend“ angelegt und ist erst nach Verifizierung durch eine zweite Person zahlungswirksam.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>IBAN *</Label><Input data-testid="bank-iban-input" value={form.iban} onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>BIC</Label><Input value={form.bic} onChange={(e) => setForm((f) => ({ ...f, bic: e.target.value }))} /></div>
              <div><Label>Bank</Label><Input value={form.bank_name} onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} /></div>
            </div>
            <div><Label>Kontoinhaber</Label><Input value={form.account_holder} onChange={(e) => setForm((f) => ({ ...f, account_holder: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Abbrechen</Button>
            <Button data-testid="bank-save-btn" disabled={busy || !form.iban} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={add}>Anfragen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!verifyFor} onOpenChange={() => setVerifyFor(null)}>
        <DialogContent data-testid="bank-verify-dialog">
          <DialogHeader><DialogTitle>Bankverbindung verifizieren</DialogTitle><DialogDescription>Methode und Nachweis sind erforderlich. Der Anforderer darf nicht selbst verifizieren.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Methode</Label>
              <Select value={vform.verification_method} onValueChange={(v) => setVform((f) => ({ ...f, verification_method: v }))}>
                <SelectTrigger data-testid="bank-verify-method"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="callback">Rückruf</SelectItem><SelectItem value="bank_letter">Bankbestätigung</SelectItem><SelectItem value="portal_confirmed">Portal bestätigt</SelectItem>
                </SelectContent></Select></div>
            <div><Label>Nachweis / Notiz *</Label><Textarea data-testid="bank-verify-note" value={vform.note} onChange={(e) => setVform((f) => ({ ...f, note: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setVerifyFor(null)}>Abbrechen</Button>
            <Button data-testid="bank-verify-save-btn" disabled={busy || !vform.verification_method || !vform.note} className="bg-green-600 hover:bg-green-700" onClick={verify}>Verifizieren</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
